import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { ShieldCheck, Camera, MapPin, Loader2, Check, AlertTriangle, Copy } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import api from "lib/api";
import { buildWsUrl } from "lib/ws";
import { SourceSender, isCaptureSupported } from "lib/webcodecsSender";
import { importServerKey, postSigned } from "lib/livenessCrypto";
import { useI18n } from "i18n";
import GestureIcon from "./GestureIcon";

// Pre-share identity gate. The FRONTEND is DUMB: it captures the camera, streams the H.264
// evidence to the recording WS (per-phase buckets), sends lightweight JPEG stills up the
// CONTROL WS (~200ms), and RENDERS whatever the backend commands (guidance, oval, flash,
// phase changes). The backend owns 100% of the decisions and the phase timing.
const FRAME_SEND_MS = 200;   // cadence of the stills sent up the control WS
const INTERRUPT_GRACE_MS = 3500;
const MAX_ATTEMPTS = 15;
const CAMERA_CONSTRAINTS = { video: { width: { max: 1280 }, height: { max: 720 }, frameRate: { max: 15 } } };
const GEO_TIMEOUT_MS = 8000;

// guidance_code → localized string (fallback PT, then the raw code).
const GUIDANCE = {
  no_face: "Posicione seu rosto dentro do círculo",
  multiple_faces: "Apenas você deve aparecer na câmera",
  too_far: "Aproxime o rosto da câmera",
  fill_oval: "Aproxime o rosto para preencher o círculo",
  too_close: "Afaste um pouco o rosto",
  not_frontal: "Olhe diretamente para a câmera",
  off_center: "Centralize seu rosto no círculo",
  blurry: "Melhore a iluminação e fique parado — a imagem está pouco nítida",
  low_confidence: "Rosto não detectado com clareza",
  ok: "Perfeito! Mantenha o rosto…",
  detector_unavailable: "Verificando…",
  decode_failed: "Ajustando a câmera…",
  move_near: "Encaixe o rosto no círculo",
  move_far: "Agora encaixe o rosto no círculo menor",
  look_at_screen: "Olhe para a tela",
  flash_fill_oval: "Aproxime-se e encaixe o rosto no círculo maior",
  flash_hold_fit: "Encaixou! Aguarde parado…",
  flash_hold: "Mantenha o rosto no círculo até os flashes pararem",
  gesture_prompt: "Faça o gesto pedido",
  gesture_hold: "Perfeito! Mantenha o gesto…",
};

function getGeolocation(requireGeo) {
  return new Promise((resolve) => {
    if (!("geolocation" in navigator)) {
      resolve({ latitude: null, longitude: null, accuracy: null, geo_error: "unsupported" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude, longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy, geo_error: "",
      }),
      (err) => resolve({
        latitude: null, longitude: null, accuracy: null,
        geo_error: (err && err.code === 1) ? "denied" : "unavailable",
      }),
      { enableHighAccuracy: true, timeout: GEO_TIMEOUT_MS, maximumAge: 0 }
    );
  });
}

export default function LivenessGate({ token, requireGeo, onPassed, onDenied }) {
  const { t } = useI18n();
  const guidanceText = useCallback(
    (code) => (code
      ? t(`liveness.guidance.${code}`, GUIDANCE[code] || code)
      : t("liveness.verifying", "Verificando…")),
    [t]
  );
  const gestureInstruction = useCallback(
    (g) => (g?.key ? t(`gesture.instr.${g.key}`, g.instruction || "") : (g?.instruction || "")),
    [t]
  );
  const [geoOk, setGeoOk] = useState(false);
  const [requestingGeo, setRequestingGeo] = useState(false);
  const [captureSupported] = useState(() => isCaptureSupported());
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [gateError, setGateError] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);

  // Everything below is RENDERED from the backend's control messages.
  const [phase, setPhase] = useState("acquire"); // acquire|depth|flashmark|gesture|checking|failed
  const [guidance, setGuidance] = useState("");
  const [good, setGood] = useState(false);
  const [ovalScale, setOvalScale] = useState(1);
  const [flashColor, setFlashColor] = useState(null);
  const [error, setError] = useState("");
  const [frozen, setFrozen] = useState(null);
  const [gesture, setGesture] = useState(null);   // {key, instruction, emoji}

  const lastGoodStillRef = useRef(null);      // newest frame the server called good (face)
  const checkingImageRef = useRef(null);      // backend-chosen sharp still shown on "Validating…"
  const lastFullFrameRef = useRef(null);      // newest good/approach full frame (with oval)
  const lastGestureStillRef = useRef(null);   // newest frame during the gesture phase
  const geoRef = useRef(null);
  const sessionRef = useRef(null);
  const keysRef = useRef(null);
  const attemptRef = useRef(1);
  const recSourceRef = useRef(1);             // current recording source_index (phase band)
  const recFactoryRef = useRef(null);
  const interruptRef = useRef(null);
  const disconnectTimerRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const senderRef = useRef(null);
  const controlRef = useRef(null);
  const frameTimerRef = useRef(null);
  const phaseRef = useRef("acquire");
  const finishingRef = useRef(false);

  const cleanup = useCallback(() => {
    if (frameTimerRef.current) { clearInterval(frameTimerRef.current); frameTimerRef.current = null; }
    try { controlRef.current?.close?.(); } catch { /* ignore */ }
    controlRef.current = null;
    try { senderRef.current?.stop?.(); } catch { /* ignore */ }
    senderRef.current = null;
    streamRef.current?.getTracks?.().forEach((tk) => tk.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  useEffect(() => {
    if (token) api.defaults.headers.common["X-Session-Token"] = token;
    return () => { delete api.defaults.headers.common["X-Session-Token"]; };
  }, [token]);

  const ensureSession = useCallback(async () => {
    if (sessionRef.current) return sessionRef.current;
    const res = await api.get(`/verify/api/status/`);
    const d = res.data || {};
    if (d.public_key && !keysRef.current) {
      try { keysRef.current = await importServerKey(d.public_key); } catch { /* unsigned */ }
    }
    if (d.active_index) attemptRef.current = d.active_index;
    sessionRef.current = { id: d.id, wsBase: "" };
    return sessionRef.current;
  }, []);

  const ensureKeys = useCallback(async () => {
    if (keysRef.current) return keysRef.current;
    await ensureSession();
    return keysRef.current;
  }, [ensureSession]);

  const enableGeo = useCallback(async () => {
    setGateError(""); setRequestingGeo(true);
    try {
      await ensureSession();
      await ensureKeys();
      const geo = await getGeolocation(requireGeo);
      geoRef.current = geo;
      const r = await postSigned(`/verify/api/geo/`, geo, keysRef.current);
      if (!r?.ok) {
        setGateError(t("liveness.geoRequired", "É necessário permitir sua localização exata para continuar. Conceda a permissão e tente novamente."));
      } else {
        setGeoOk(true);
      }
    } catch (e) {
      setGateError(e?.response?.data?.reason === "geo_required"
        ? t("liveness.geoRequired", "É necessário permitir sua localização exata para continuar. Conceda a permissão e tente novamente.")
        : (e?.message || e?.response?.data?.detail || t("liveness.geoValidateFailed", "Não foi possível validar a localização.")));
    } finally { setRequestingGeo(false); }
  }, [requireGeo, ensureSession, ensureKeys, t]);

  const submitNoGeo = useCallback(async () => {
    setDeclineOpen(false); setGateError(""); setRequestingGeo(true);
    try {
      await ensureSession(); await ensureKeys();
      const geo = { latitude: null, longitude: null, accuracy: null, geo_error: "declined_by_user" };
      geoRef.current = geo;
      const r = await postSigned(`/verify/api/geo/`, geo, keysRef.current);
      if (!r?.ok) setGateError(t("liveness.geoNoContinue", "Não foi possível continuar sem a localização."));
      else setGeoOk(true);
    } catch (e) {
      setGateError(e?.message || t("liveness.cannotContinue", "Não foi possível continuar."));
    } finally { setRequestingGeo(false); }
  }, [ensureSession, ensureKeys, t]);

  const grabStill = useCallback(() => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return null;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.7);
  }, []);

  const grabWithOval = useCallback((color) => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return null;
    c.width = v.videoWidth; c.height = v.videoHeight;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, c.width, c.height);
    const boxAspect = 3 / 4;
    let visW, visH;
    if (c.width / c.height > boxAspect) { visH = c.height; visW = boxAspect * c.height; }
    else { visW = c.width; visH = c.width / boxAspect; }
    const rx = Math.min(c.width * 0.49, 0.31 * visW);
    const ry = Math.min(c.height * 0.49, 0.39 * visH);
    ctx.save();
    ctx.strokeStyle = color || "#34d399";
    ctx.lineWidth = Math.max(3, c.width * 0.006);
    ctx.setLineDash([c.width * 0.02, c.width * 0.013]);
    ctx.beginPath();
    ctx.ellipse(c.width / 2, c.height / 2, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
    return c.toDataURL("image/jpeg", 0.7);
  }, []);

  // Backend says capture is done and it is now SCORING → show the "validando" screen
  // immediately (so the slow AI scoring doesn't look like the last phase hanging) and stop
  // the capture. The control socket stays open to receive the final 'complete'.
  const showChecking = useCallback((overrideImage) => {
    // The capture is DONE — we're on the final-verification screen. Mark the phase FIRST
    // (before stopping the recorder, which closes its socket and fires a disconnect), and
    // cancel any pending interrupt: a connection drop here is irrelevant and must NOT restart
    // the whole capture. (finishingRef is left for submitComplete to own — it dedups the POST.)
    phaseRef.current = "checking";
    if (disconnectTimerRef.current) { clearTimeout(disconnectTimerRef.current); disconnectTimerRef.current = null; }
    if (frameTimerRef.current) { clearInterval(frameTimerRef.current); frameTimerRef.current = null; }
    try { senderRef.current?.stop?.(); } catch { /* ignore */ }
    senderRef.current = null;
    // Show the BACKEND-chosen sharp small-oval still (the Face/Full photo) if it sent one;
    // remember it so submitComplete's re-call keeps showing the same frame.
    if (overrideImage) checkingImageRef.current = overrideImage;
    const faceStill = checkingImageRef.current || lastGoodStillRef.current || grabStill();
    setFrozen(faceStill || lastGestureStillRef.current);
    try { videoRef.current?.pause?.(); } catch { /* ignore */ }
    setPhase("checking"); phaseRef.current = "checking";
    setGuidance(t("liveness.checking", "Validando…"));
  }, [grabStill, t]);

  // Backend verdict is ready → submit the final complete (one POST). The stored artifacts
  // use the best stills we remembered from the server's state.
  const submitComplete = useCallback(async () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    showChecking();   // ensure the checking screen even if the 'checking' message was missed
    try { controlRef.current?.close?.(); } catch { /* ignore */ }
    controlRef.current = null;
    const faceStill = lastGoodStillRef.current || grabStill();
    const gestureImage = lastGestureStillRef.current || faceStill;
    try {
      const r = await postSigned(`/verify/api/complete/`, {
        image: faceStill || gestureImage,
        full_image: lastFullFrameRef.current || grabWithOval("#34d399") || faceStill,
        gesture_image: gestureImage,
      }, keysRef.current);
      if (r?.passed) { cleanup(); onPassed(r?.redirect); return; }
      cleanup();
      onDenied?.(r?.redirect, r?.stage);
    } catch (e) {
      if (e?.response?.status === 409) { cleanup(); onDenied?.(null, "finalized"); return; }
      // /complete failed (e.g. an origin/CDN timeout under load). The whole routine is already
      // recorded, so fall back to the interrupt path, which finalizes straight from the buckets
      // instead of dead-ending the user on an error.
      finishingRef.current = false;
      if (interruptRef.current) { interruptRef.current(); return; }
      setPhase("failed"); phaseRef.current = "failed";
      setError(e?.response?.data?.detail || t("liveness.completeFailed", "Falha ao concluir a verificação."));
    }
  }, [showChecking, grabStill, grabWithOval, cleanup, onPassed, onDenied, t]);

  // Apply a backend `state` render command.
  const applyState = useCallback((m) => {
    const ph = m.phase || "acquire";
    setPhase(ph); phaseRef.current = ph;
    setGood(!!m.good);
    setOvalScale(typeof m.oval_scale === "number" ? m.oval_scale : 1);
    setFlashColor(m.flash_color || null);
    if (m.gesture) setGesture(m.gesture);
    setGuidance(guidanceText(m.guidance_code));
    // Remember the best stills for the final complete POST (bookkeeping, not a decision).
    if (m.good && ph !== "gesture") lastGoodStillRef.current = grabStill();
    // Full frame (with oval): keep the latest during the depth/approach phase — the person
    // is closest there — falling back to any good-face frame in acquire.
    if (ph === "depth" || (ph === "acquire" && m.good)) {
      lastFullFrameRef.current = grabWithOval("#34d399");
    }
    if (ph === "gesture") lastGestureStillRef.current = grabStill();
  }, [guidanceText, grabStill, grabWithOval]);

  // Open the CONTROL WebSocket and stream stills up; render everything it commands.
  const openControl = useCallback((sessionId, wsBase) => {
    let ws;
    try {
      ws = new WebSocket(buildWsUrl(`/verify/ws/${sessionId}/control/`, wsBase, token));
    } catch { return; }
    controlRef.current = ws;
    ws.onopen = () => {
      try { ws.send(JSON.stringify({ type: "ready" })); } catch { /* ignore */ }
      if (frameTimerRef.current) clearInterval(frameTimerRef.current);
      frameTimerRef.current = setInterval(() => {
        if (finishingRef.current || ws.readyState !== WebSocket.OPEN) return;
        const img = grabStill();
        if (img) { try { ws.send(JSON.stringify({ type: "frame", image: img })); } catch { /* ignore */ } }
      }, FRAME_SEND_MS);
    };
    ws.onmessage = (ev) => {
      let m; try { m = JSON.parse(ev.data); } catch { return; }
      if (m.type === "state") applyState(m);
      else if (m.type === "phase") {
        // Re-point the recorder to this phase's OWN bucket (no teardown of the encoder).
        recSourceRef.current = m.source_index;
        if (senderRef.current && recFactoryRef.current) senderRef.current.setSource(recFactoryRef.current);
      } else if (m.type === "checking") showChecking(m.image);
      else if (m.type === "complete") submitComplete();
      else if (m.type === "fail") { cleanup(); onDenied?.(null, m.reason || "fail"); }
    };
    ws.onclose = () => {
      if (finishingRef.current || phaseRef.current === "checking") return;
      // Control dropped mid-capture → treat as an interruption (restart the attempt).
      if (interruptRef.current) interruptRef.current();
    };
    ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
  }, [token, applyState, showChecking, submitComplete, cleanup, onDenied]);

  const startCamera = useCallback(async () => {
    setError(""); setFrozen(null); setOvalScale(1); setFlashColor(null); setGesture(null);
    setPhase("acquire"); phaseRef.current = "acquire";
    setGuidance(t("liveness.startingCamera", "Iniciando a câmera…"));
    lastGoodStillRef.current = null; lastGestureStillRef.current = null; checkingImageRef.current = null;
    finishingRef.current = false; setGood(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      const { id: sessionId, wsBase } = await ensureSession();
      const t0 = Date.now();
      const track = stream.getVideoTracks()[0];
      // Start recording on the depth bucket; the backend re-points us per phase.
      recSourceRef.current = attemptRef.current * 10 + 1;
      recFactoryRef.current = () => new WebSocket(
        `${buildWsUrl(`/recording/ws/${sessionId}/liveness/${recSourceRef.current}/`, wsBase, token)}&t0=${t0}`);
      const sender = new SourceSender({
        track: track.clone(), fps: 12, bitrate: 1_200_000, label: "liveness",
        openWs: recFactoryRef.current,
        onState: ({ connected }) => {
          // Once the capture is done (finishing) or we're on the final-verification screen,
          // a recording-socket drop is irrelevant — never restart the whole capture there.
          if (finishingRef.current || phaseRef.current === "checking") return;
          if (connected) {
            if (disconnectTimerRef.current) { clearTimeout(disconnectTimerRef.current); disconnectTimerRef.current = null; }
          } else if (!disconnectTimerRef.current) {
            disconnectTimerRef.current = setTimeout(() => {
              disconnectTimerRef.current = null;
              // Re-check at fire time: the grace window may have crossed into checking/finishing.
              if (finishingRef.current || phaseRef.current === "checking") return;
              if (interruptRef.current) interruptRef.current();
            }, INTERRUPT_GRACE_MS);
          }
        },
      });
      sender.start();
      senderRef.current = sender;
      openControl(sessionId, wsBase);
    } catch (e) {
      setPhase("failed"); phaseRef.current = "failed";
      setError(e?.name === "NotAllowedError"
        ? t("liveness.cameraDenied", "Permita o acesso à câmera para continuar.")
        : (e?.response?.data?.detail || t("liveness.cameraFailed", "Não foi possível iniciar a câmera.")));
      cleanup();
    }
  }, [ensureSession, openControl, cleanup, t]);

  const onInterruption = useCallback(async () => {
    if (finishingRef.current) return;
    const idx = attemptRef.current;
    if (frameTimerRef.current) { clearInterval(frameTimerRef.current); frameTimerRef.current = null; }
    try { controlRef.current?.close?.(); } catch { /* ignore */ }
    controlRef.current = null;
    try { senderRef.current?.stop?.(); } catch { /* ignore */ }
    senderRef.current = null;
    streamRef.current?.getTracks?.().forEach((tk) => tk.stop());
    streamRef.current = null;
    setGood(false); setFrozen(null); setFlashColor(null);
    let res = null;
    try { res = await postSigned(`/verify/api/interrupt/`, { index: idx }, keysRef.current); } catch { /* best-effort */ }
    // The whole routine was already recorded across attempts → the backend finalized it from
    // the buckets. Go STRAIGHT to the verdict instead of restarting the capture from scratch.
    if (res?.finalized) {
      finishingRef.current = true;
      showChecking();
      cleanup();
      if (res.passed) onPassed(res.redirect); else onDenied?.(res.redirect, "finalized");
      return;
    }
    attemptRef.current = idx + 1;
    if (attemptRef.current > MAX_ATTEMPTS) {
      setPhase("failed"); phaseRef.current = "failed";
      setError(t("liveness.tooManyInterruptions", "Muitas interrupções na conexão. Verifique sua internet e tente novamente."));
      return;
    }
    setPhase("acquire"); phaseRef.current = "acquire";
    setGuidance(t("liveness.connectionInterrupted", "Conexão interrompida — reiniciando a verificação…"));
    startCamera();
  }, [startCamera, showChecking, cleanup, onPassed, onDenied, t]);

  useEffect(() => { interruptRef.current = onInterruption; }, [onInterruption]);

  const openCamera = useCallback(() => {
    if (!isCaptureSupported()) return;
    setCameraOpen(true); startCamera();
  }, [startCamera]);

  const copyUrl = useCallback(() => {
    try { navigator.clipboard?.writeText(window.location.href); } catch { /* ignore */ }
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 1500);
  }, []);

  const stepDot = (n, done, active) => (
    <div className={`mt-0.5 rounded-full h-6 w-6 shrink-0 flex items-center justify-center text-xs font-semibold ${
      done ? "bg-emerald-500 text-white" : active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
      {done ? <Check className="h-4 w-4" /> : n}
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes livenessScan {
          0%   { transform: translateY(0); opacity: 0.35; }
          10%  { opacity: 1; }
          50%  { transform: translateY(730%); opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translateY(0); opacity: 0.35; }
        }
        .liveness-scanbar { animation: livenessScan 2.2s ease-in-out infinite; }
      `}</style>
      <Card><CardContent className="p-6 space-y-5">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">{t("liveness.title", "Verificação de identidade")}</h2>
        </div>
        {captureSupported ? (
          <>
            <p className="text-sm text-muted-foreground">
              {t("liveness.intro", "Este exame exige uma verificação de identidade (liveness) antes de você compartilhar a tela. Conclua os dois passos abaixo para continuar.")}
            </p>

            <div className="flex items-start gap-3">
              {stepDot(1, geoOk, !geoOk)}
              <div className="flex-1">
                <p className="text-sm font-medium">{t("liveness.stepLocation", "Localização")}</p>
                <p className="text-xs text-muted-foreground mb-2">
                  {requireGeo ? t("liveness.geoRequiredHint", "Obrigatória para continuar.") : t("liveness.geoOptionalHint", "Será solicitada; você pode recusar.")}
                </p>
                {geoOk ? (
                  <span className="text-sm text-emerald-500 inline-flex items-center gap-1">
                    <Check className="h-4 w-4" />
                    {geoRef.current && geoRef.current.geo_error === "declined_by_user"
                      ? t("liveness.geoNotProvided", "Localização não informada") : t("liveness.geoEnabled", "Localização habilitada")}
                  </span>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button onClick={enableGeo} disabled={requestingGeo}>
                      {requestingGeo
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("liveness.requesting", "Solicitando…")}</>
                        : <><MapPin className="h-4 w-4 mr-2" /> {t("liveness.enableGeo", "Habilitar geolocalização")}</>}
                    </Button>
                    {!requireGeo && (
                      <Button variant="ghost" onClick={() => setDeclineOpen(true)} disabled={requestingGeo}>
                        {t("liveness.declineGeo", "Não informar localização")}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              {stepDot(2, false, geoOk)}
              <div className="flex-1">
                <p className={`text-sm font-medium ${geoOk ? "" : "text-muted-foreground"}`}>{t("liveness.faceCapture", "Captura facial")}</p>
                <p className="text-xs text-muted-foreground mb-2">
                  {t("liveness.captureHint", "Aproxime o rosto do círculo, olhe para a câmera e mantenha os olhos abertos. Todo o processo é gravado.")}
                </p>
                <Button onClick={openCamera} disabled={!geoOk}>
                  <Camera className="h-4 w-4 mr-2" /> {t("liveness.startCapture", "Iniciar captura facial")}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                  {t("liveness.unsupportedTitle", "Navegador não suportado")}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("liveness.unsupportedBody", "Este navegador não suporta a captura de vídeo necessária (WebCodecs). Copie o link abaixo e abra em outro navegador atualizado (Chrome ou Edge, no computador ou Android).")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5">
              <code className="flex-1 text-xs break-all">{typeof window !== "undefined" ? window.location.href : ""}</code>
              <button type="button" onClick={copyUrl}
                className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                title={t("common.copy", "Copiar")}>
                {copiedUrl
                  ? <><Check className="h-4 w-4 text-emerald-500" /> {t("common.copied", "Copiado")}</>
                  : <><Copy className="h-4 w-4" /> {t("common.copy", "Copiar")}</>}
              </button>
            </div>
          </div>
        )}

        {gateError && <p className="text-sm text-red-500">{gateError}</p>}
      </CardContent></Card>

      {declineOpen && createPortal(
        <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-sm rounded-2xl bg-card border border-border shadow-xl p-6 space-y-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-amber-500" />
              <h3 className="text-base font-semibold">{t("liveness.declineTitle", "Não informar localização?")}</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              {t("liveness.declineBody", "Você tem certeza que não deseja informar sua localização? A recusa fica registrada na sua verificação.")}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeclineOpen(false)}>{t("liveness.cancel", "Cancelar")}</Button>
              <Button onClick={submitNoGeo} disabled={requestingGeo}>{t("liveness.confirmDecline", "Sim, não informar")}</Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Flashmark: full-screen controlled illumination dictated by the backend. */}
      {flashColor && createPortal(
        <div className="fixed inset-0 z-[120] flex items-center justify-center transition-colors duration-100"
          style={{ backgroundColor: flashColor }}>
          <p className="px-4 text-center text-lg font-semibold text-black/70 mix-blend-overlay">
            {t("liveness.lookAtScreen", "Olhe para a tela")}
          </p>
        </div>,
        document.body
      )}

      {cameraOpen && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <p className={`text-center text-lg sm:text-xl font-semibold leading-snug ${good ? "text-emerald-500" : "text-foreground"}`}>
                {guidance || t("liveness.faceCapture", "Captura facial")}
              </p>
            </div>
            <div className="p-5 space-y-4">
              {phase === "gesture" && gesture && (
                <div className="rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 flex items-center gap-3">
                  <span className="shrink-0 rounded-lg bg-white p-1 text-slate-900">
                    <GestureIcon gesture={gesture.key} className="h-16 w-16" />
                  </span>
                  <p className="text-sm font-medium">{gestureInstruction(gesture)}</p>
                </div>
              )}
              <div className="relative mx-auto w-full aspect-[3/4] max-w-xs bg-black rounded-xl overflow-hidden">
                <video ref={videoRef} autoPlay muted playsInline
                  className={`absolute inset-0 w-full h-full object-cover -scale-x-100 ${frozen ? "hidden" : ""}`} />
                {frozen && <img src={frozen} alt={t("liveness.altCapture", "captura")} className="absolute inset-0 w-full h-full object-cover -scale-x-100" />}
                {phase !== "gesture" && phase !== "checking" && phase !== "failed" && (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                  <div
                    style={{ transform: `scale(${ovalScale})` }}
                    className={`h-[78%] w-[62%] rounded-[50%] border-4 border-dashed transition-all duration-500 ${phase === "depth" ? "border-amber-400" : good ? "border-emerald-400" : "border-sky-400/80"}`}
                  />
                </div>
                )}
                {(phase === "checking" || phase === "failed") && (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    {/* Same corner-bracket detail on both the validating and error screens. */}
                    <div className="absolute inset-4">
                      {["top-0 left-0 border-t-2 border-l-2", "top-0 right-0 border-t-2 border-r-2",
                        "bottom-0 left-0 border-b-2 border-l-2", "bottom-0 right-0 border-b-2 border-r-2"
                      ].map((pos, i) => (
                        <span key={i} className={`absolute h-6 w-6 ${phase === "failed" ? "border-red-400" : "border-sky-400"} ${pos}`} />
                      ))}
                    </div>
                    {phase === "checking" && (<>
                      <div className="liveness-scanbar absolute left-0 right-0 h-[12%]"
                        style={{ background: "linear-gradient(to bottom, rgba(239,68,68,0) 0%, rgba(239,68,68,0.55) 50%, rgba(239,68,68,0) 100%)", boxShadow: "0 0 20px 5px rgba(239,68,68,0.45)" }} />
                      <div className="absolute inset-x-0 top-1.5 flex justify-center">
                        <span className="rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white tracking-wide">{t("liveness.checking", "Validando…")}</span>
                      </div>
                    </>)}
                  </div>
                )}
              </div>
              {error && <p className="text-sm text-red-500 text-center">{error}</p>}
              {phase === "failed" && (
                <Button onClick={startCamera} variant="outline" className="w-full">{t("liveness.retry", "Tentar novamente")}</Button>
              )}
            </div>
            <canvas ref={canvasRef} className="hidden" />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
